import messages from 'messages';
import { ProductCardsProps } from '@raisin/components-lib';
import { getResizedImageUrl } from '@raisin/cp-utils';
import { Product } from 'api/dbs/models';
import { getInterestRateType } from 'utils/getInterestRateType';
import { AppLocale } from 'utils/parseAnyLocale';
import { getMarketingProductType } from '../../../../../utils/getMarketingProductType';
import { getMaturiyTranslationMessage } from '../../../../../utils/getMaturityTranslationMessage';
import { CsProductCardsProduct, CsProductCardsProductLabel } from '../../../types';
import { isCustomLabel } from '../getProductLabels/labelGenerators';

const TYPE_KEY = {
  OVERNIGHT: messages.productTypeOvernight,
  TERM: messages.productTypeTerm,
  NOTICE: messages.productTypeNotice,
};

export const createProductParser =
  (dependencies: {
    dbsProducts: Record<Product['id'], Product>;
    linkGenerator: (id: string) => string;
    formatMessage;
    getProductLabels: (
      product: Product,
      value: CsProductCardsProduct,
    ) => CsProductCardsProductLabel[] | undefined;
    locale: AppLocale;
  }) =>
  (value: CsProductCardsProduct) => {
    const id = value.product_id;
    const dbs = dependencies.dbsProducts[id];

    const bankLogo = getResizedImageUrl({
      resizableUrl: dbs.deposit_taking_bank.logo_url || null,
      fallbackUrl: dbs.deposit_taking_bank.bank_logo_url,
      params: { height: 47 },
    });

    const rateType = getInterestRateType(dependencies.locale);
    const depositType = dependencies.formatMessage(TYPE_KEY[getMarketingProductType(dbs)]);
    const maturityKey = getMaturiyTranslationMessage(dbs, dependencies.locale);
    const maturity = maturityKey ? dependencies.formatMessage(...maturityKey) : undefined;
    const perAnnumText = dependencies.formatMessage(messages.perYear);
    const href = dependencies.linkGenerator(dbs.id);
    const labels =
      dependencies.getProductLabels(dbs, value) ??
      [isCustomLabel({} as unknown as Product, value), ...value.label].filter(Boolean);
    return {
      id: dbs.id,
      bankName: dbs.deposit_taking_bank.name,
      bankLogoAlt: dependencies.formatMessage(messages.bankLogoAlt, {
        bankName: dbs.deposit_taking_bank.name,
      }),
      interestRate: parseFloat(dbs.interest_rates[rateType] || '0'),
      countryName: dbs.deposit_taking_bank.statutory_deposit_guarantee_scheme.country?.name ?? '',
      countryRating: dbs.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.country?.rating,
      labels,
      perAnnumText,
      href,
      depositType,
      bankLogo,
      ...(!!maturity && { maturity }),
      showDepositTypeInMaturity: getMarketingProductType(dbs) === 'NOTICE',
    } as ProductCardsProps['sections'][number]['products'][number];
  };
